import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Checkbox,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  InsertDriveFile as FileIcon,
  Code as CodeIcon,
  Description as DocIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { fileService, FileInfo, FileSearchFilters, FileMetadata } from '../../services/fileService';

interface ChatFileSearchPanelProps {
  onSelectFiles: (files: FileInfo[], allPanelFileIds: string[]) => void;
  selectedFileIds: string[];
}

const ChatFileSearchPanel: React.FC<ChatFileSearchPanelProps> = ({
  onSelectFiles,
  selectedFileIds,
}) => {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [metadata, setMetadata] = useState<FileMetadata>({
    categories: [],
    tags: [],
    fileTypes: [],
  });
  const [activeFilters, setActiveFilters] = useState<FileSearchFilters>({});
  const [localSelectedIds, setLocalSelectedIds] = useState<Set<string>>(
    new Set(selectedFileIds)
  );
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const requestCounterRef = useRef<number>(0);

  // Load metadata on mount
  useEffect(() => {
    const loadMetadata = async () => {
      const meta = await fileService.getFileMetadata();
      setMetadata(meta);
    };
    loadMetadata();
  }, []);

  // Load files on mount and when filters change
  const loadFiles = useCallback(async (query: string, filters: FileSearchFilters) => {
    const currentRequest = ++requestCounterRef.current;
    setIsLoading(true);
    try {
      if (query.trim()) {
        const results = await fileService.searchFiles(query, 20, filters);
        if (currentRequest === requestCounterRef.current) {
          setFiles(results);
        }
      } else {
        const results = await fileService.getFilesForSelection(filters);
        if (currentRequest === requestCounterRef.current) {
          setFiles(results);
        }
      }
    } catch (error) {
      console.error('Failed to load files:', error);
      if (currentRequest === requestCounterRef.current) {
        setFiles([]);
      }
    } finally {
      if (currentRequest === requestCounterRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Debounced search and filter-based loading
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      loadFiles(searchQuery, activeFilters);
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [searchQuery, activeFilters, loadFiles]);

  // Focus search input on mount
  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  // Sync localSelectedIds when selectedFileIds prop changes
  useEffect(() => {
    setLocalSelectedIds(new Set(selectedFileIds));
  }, [selectedFileIds]);

  const toggleFilter = (type: keyof FileSearchFilters, value: string) => {
    setActiveFilters(prev => {
      const current = prev[type] || [];
      const isActive = current.includes(value);
      return {
        ...prev,
        [type]: isActive
          ? current.filter(v => v !== value)
          : [...current, value],
      };
    });
  };

  const clearFilters = () => {
    setActiveFilters({});
    setSearchQuery('');
  };

  const hasActiveFilters = Object.values(activeFilters).some(v => v && v.length > 0);

  const handleToggleFile = (file: FileInfo) => {
    const isSelected = localSelectedIds.has(file.id);
    const next = new Set(localSelectedIds);
    if (isSelected) {
      next.delete(file.id);
    } else {
      next.add(file.id);
    }

    setLocalSelectedIds(next);

    // Notify parent of selection change (outside state updater to avoid StrictMode double-call)
    const selectedFiles = files.filter(f => next.has(f.id));
    // Also include files that were previously selected but not in current list
    const previouslySelected = selectedFileIds
      .filter(id => next.has(id) && !files.some(f => f.id === id))
      .map(id => ({ id, name: 'Unknown', title: 'Unknown', path: '', type: 'document' as const }));

    onSelectFiles([...selectedFiles, ...previouslySelected], files.map(f => f.id));
  };

  const getFileIcon = (type: string) => {
    switch (type) {
      case 'code':
        return <CodeIcon sx={{ fontSize: 18, color: 'text.secondary' }} />;
      case 'document':
        return <DocIcon sx={{ fontSize: 18, color: 'primary.main' }} />;
      default:
        return <FileIcon sx={{ fontSize: 18, color: 'text.secondary' }} />;
    }
  };

  const getFileExtension = (fileName: string): string => {
    const parts = fileName.split('.');
    return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
  };

  return (
    <Paper
      elevation={3}
      sx={{
        maxHeight: 400,
        minWidth: 320,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        mb: 1,
      }}
    >
      {/* Search Input */}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          inputRef={searchInputRef}
          fullWidth
          size="small"
          placeholder={t('chat.fileSearchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              fontSize: '0.875rem',
            },
          }}
        />
      </Box>

      {/* Filter Chips */}
      {(metadata.categories.length > 0 || metadata.fileTypes.length > 0) && (
        <Box
          sx={{
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            maxHeight: 80,
            overflow: 'auto',
          }}
        >
          {metadata.categories.map(cat => (
            <Chip
              key={`cat-${cat}`}
              label={cat}
              size="small"
              variant={activeFilters.categories?.includes(cat) ? 'filled' : 'outlined'}
              color={activeFilters.categories?.includes(cat) ? 'primary' : 'default'}
              onClick={() => toggleFilter('categories', cat)}
              sx={{ height: 24, fontSize: '0.75rem' }}
            />
          ))}
          {metadata.fileTypes.map(ft => (
            <Chip
              key={`ft-${ft}`}
              label={ft.toUpperCase()}
              size="small"
              variant={activeFilters.fileTypes?.includes(ft) ? 'filled' : 'outlined'}
              color={activeFilters.fileTypes?.includes(ft) ? 'secondary' : 'default'}
              onClick={() => toggleFilter('fileTypes', ft)}
              sx={{ height: 24, fontSize: '0.75rem' }}
            />
          ))}
          {metadata.tags.slice(0, 8).map(tag => (
            <Chip
              key={`tag-${tag}`}
              label={tag}
              size="small"
              variant={activeFilters.tags?.includes(tag) ? 'filled' : 'outlined'}
              color={activeFilters.tags?.includes(tag) ? 'info' : 'default'}
              onClick={() => toggleFilter('tags', tag)}
              sx={{ height: 24, fontSize: '0.75rem' }}
            />
          ))}
          {hasActiveFilters && (
            <Chip
              icon={<CloseIcon sx={{ fontSize: 14 }} />}
              label={t('common.clearAll')}
              size="small"
              onClick={clearFilters}
              sx={{ height: 24, fontSize: '0.75rem' }}
            />
          )}
        </Box>
      )}

      {/* File List */}
      <Box sx={{ flex: 1, overflow: 'auto', maxHeight: 250 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : files.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery || hasActiveFilters
                ? t('chat.noFilesFound')
                : t('chat.noFiles')}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {files.map(file => (
              <ListItemButton
                key={file.id}
                onClick={() => handleToggleFile(file)}
                sx={{
                  py: 0.75,
                  px: 1.5,
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
              >
                <Checkbox
                  checked={localSelectedIds.has(file.id)}
                  size="small"
                  sx={{ p: 0.5, mr: 1 }}
                />
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {getFileIcon(file.type)}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          fontSize: '0.8125rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {file.title || file.name}
                      </Typography>
                      {getFileExtension(file.name) && (
                        <Chip
                          label={getFileExtension(file.name)}
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: '0.625rem',
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        display: 'flex',
                        gap: 0.5,
                        alignItems: 'center',
                        fontSize: '0.6875rem',
                      }}
                    >
                      {file.category && <span>{file.category}</span>}
                      {file.category && file.tags && file.tags.length > 0 && <span>·</span>}
                      {file.tags && file.tags.length > 0 && (
                        <span>{file.tags.slice(0, 2).join(', ')}</span>
                      )}
                      {file.uploadedAt && (
                        <>
                          <span>·</span>
                          <span>
                            {new Date(file.uploadedAt).toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'ja-JP', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            })}
                          </span>
                        </>
                      )}
                    </Typography>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>

      {/* Footer */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="text.secondary">
          {t('chat.filesCount', { count: files.length })}
          {localSelectedIds.size > 0 && ` · ${t('chat.filesSelected', { count: localSelectedIds.size })}`}
        </Typography>
      </Box>
    </Paper>
  );
};

export default ChatFileSearchPanel;
